import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getEventLearningObjectives,
  createLearningObjective,
  updateLearningObjective,
  deleteLearningObjective
} from '../../services/landingPageService';

const { FiPlus, FiEdit2, FiTrash2, FiTarget, FiX, FiMove } = FiIcons;

export default function LearningObjectivesTab({ eventId }) {
  const [objectives, setObjectives] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadObjectives();
  }, [eventId]);

  async function loadObjectives() {
    try {
      setLoading(true);
      const { data, error } = await getEventLearningObjectives(eventId);
      if (error) throw error;
      setObjectives(data || []);
    } catch (error) {
      console.error('[LearningObjectivesTab] Error loading objectives:', error);
      toast.error('Failed to load learning objectives');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(objective) {
    setEditingObjective(objective);
    setShowModal(true);
  }

  function handleAdd() {
    setEditingObjective(null);
    setShowModal(true);
  }

  async function handleDelete(objectiveId) {
    if (!window.confirm('Are you sure you want to delete this learning objective?')) return;

    try {
      const { error } = await deleteLearningObjective(objectiveId);
      if (error) throw error;

      await loadObjectives();
      toast.success('Learning objective deleted');
    } catch (error) {
      console.error('[LearningObjectivesTab] Error deleting objective:', error);
      toast.error('Failed to delete learning objective');
    }
  }

  async function handleSave(objectiveData) {
    try {
      if (editingObjective) {
        const { error } = await updateLearningObjective(editingObjective.id, objectiveData);
        if (error) throw error;
        toast.success('Learning objective updated');
      } else {
        const maxOrder = objectives.reduce((max, obj) => Math.max(max, obj.display_order || 0), 0);
        const { error } = await createLearningObjective({
          ...objectiveData,
          event_id: eventId,
          display_order: maxOrder + 1
        });
        if (error) throw error;
        toast.success('Learning objective created');
      }

      setShowModal(false);
      setEditingObjective(null);
      await loadObjectives();
    } catch (error) {
      console.error('[LearningObjectivesTab] Error saving objective:', error);
      toast.error('Failed to save learning objective');
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Learning Objectives</h2>
          <p className="text-sm text-gray-500 mt-1">
            Define what attendees will learn from this event
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add Objective
        </button>
      </div>

      {/* Objectives List */}
      <div className="space-y-3">
        {objectives.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <SafeIcon icon={FiTarget} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No learning objectives added yet</p>
            <button
              onClick={handleAdd}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first objective
            </button>
          </div>
        ) : (
          objectives.map((objective, index) => (
            <div key={objective.id} className="bg-white rounded-lg shadow-sm p-4 flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
                {index + 1}
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900">{objective.title}</h4>
                {objective.description && (
                  <p className="text-sm text-gray-600 mt-1">{objective.description}</p>
                )}
                {objective.icon && (
                  <span className="text-xs text-gray-400 mt-1 inline-block">Icon: {objective.icon}</span>
                )}
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleEdit(objective)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(objective.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <ObjectiveModal
          objective={editingObjective}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingObjective(null);
          }}
        />
      )}
    </div>
  );
}

function ObjectiveModal({ objective, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: objective?.title || '',
    description: objective?.description || '',
    icon: objective?.icon || ''
  });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    await onSave(formData);
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {objective ? 'Edit Learning Objective' : 'Add Learning Objective'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Understand fundamental investment principles"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Additional details about this objective..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon Name (optional)
            </label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., check-circle, target, book"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use icon names like: check, star, book, target, etc.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : objective ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
